import { useCallback, useEffect, useMemo, useState } from "react";
import { BadgeCheck, CheckCircle2, History, Loader2, RotateCcw, Send, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CourseProgressStageLayout from "@/components/course-progress/CourseProgressStageLayout";
import WorkflowHistoryTimeline from "@/components/WorkflowHistoryTimeline";
import { getCourseProgressUrl } from "@/components/course-progress/courseProgressNavigation";
import { studentUSIService } from "@/services/studentUSIService";
import ToastHelper from "@/utils/toastHelper";

const statusClass = (status = "") => {
  const lower = String(status).toLowerCase();
  if (lower.includes("completed") || lower.includes("verified") || lower.includes("exempt")) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (lower.includes("pending")) return "border-amber-200 bg-amber-50 text-amber-700";
  if (lower.includes("action") || lower.includes("returned") || lower.includes("incorrect")) return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
};

export default function USINumberStage({ mode = "student" }) {
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const isAdmin = mode === "admin";
  const studentBookingId = Number(params.studentBookingId || params.id || state?.studentBookingId || 0);
  const courseUrl = state?.courseProgressUrl || getCourseProgressUrl(mode, studentBookingId);
  const [workflow, setWorkflow] = useState(null);
  const [usiNumber, setUsiNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [showHistory, setShowHistory] = useState(true);

  const loadUSI = useCallback(async () => {
    if (!studentBookingId) {
      setMessage("Student booking is required before USI can be opened.");
      setLoading(false);
      return;
    }
    try {
      setMessage("");
      const data = await studentUSIService.get(studentBookingId);
      setWorkflow(data);
      setUsiNumber(data?.usiNumber || data?.USINumber || "");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to load USI workflow.");
    } finally {
      setLoading(false);
    }
  }, [studentBookingId]);

  useEffect(() => { loadUSI(); }, [loadUSI]);

  const buttons = useMemo(() => workflow?.isLocked === true
    ? []
    : (workflow?.buttonList || []).filter((button) => !button.hideButton && !button.HideButton), [workflow]);
  const canEdit = Boolean(workflow?.canEdit ?? workflow?.CanEdit);
  const canEditAdmin = isAdmin && canEdit;
  const displayStatus = workflow?.displayStatus || workflow?.DisplayStatus || "Available";
  const statusName = workflow?.usiStatusName || workflow?.USIStatusName || displayStatus;
  const history = workflow?.history || workflow?.History || [];

  const submitUSI = async () => {
    if (!usiNumber.trim()) {
      setMessage("Please enter the USI number.");
      return;
    }
    setSaving("submit");
    try {
      const data = await studentUSIService.submit({
        studentBookingId,
        studentEnrollmentId: workflow?.studentEnrollmentId || workflow?.StudentEnrollmentId || state?.studentEnrollmentId || 0,
        usiNumber: usiNumber.trim(),
        remarks,
      });
      setWorkflow(data);
      setRemarks("");
      ToastHelper.success("USI submitted for verification.");
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      setMessage(error?.response?.data?.message || "Unable to submit USI.");
    } finally {
      setSaving("");
    }
  };

  const runAdminAction = async (button) => {
    const next = Number(button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo);
    const text = button.text ?? button.Text ?? "Update";
    const needsRemarks = text.toLowerCase().includes("return") || text.toLowerCase().includes("incorrect");
    const actionRemarks = needsRemarks ? window.prompt("Enter remarks for the student:") : "";
    if (actionRemarks === null) return;
    if (needsRemarks && !actionRemarks.trim()) {
      ToastHelper.error("Remarks are required.");
      return;
    }
    setSaving(String(next));
    try {
      const data = await studentUSIService.workflow({
        studentUSIId: workflow?.studentUSIId || workflow?.StudentUSIId,
        studentBookingId,
        nextUSIStatusNo: next,
        remarks: actionRemarks,
      });
      setWorkflow(data);
      ToastHelper.success(`${text} saved.`);
      navigate(courseUrl, { state: { focusCurrentStep: true } });
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save USI workflow.");
    } finally {
      setSaving("");
    }
  };

  const saveAdminUSI = async () => {
    if (!canEditAdmin || !usiNumber.trim()) {
      setMessage("Please enter the USI number.");
      return;
    }
    setSaving("usi");
    try {
      const data = await studentUSIService.adminUpdate({
        studentBookingId,
        studentEnrollmentId: workflow?.studentEnrollmentId || workflow?.StudentEnrollmentId || state?.studentEnrollmentId || 0,
        usiNumber: usiNumber.trim(),
        remarks: "USI updated by staff.",
      });
      setWorkflow(data);
      ToastHelper.success("USI saved successfully.");
    } catch (error) {
      ToastHelper.apiError(error, "Unable to save USI.");
    } finally {
      setSaving("");
    }
  };

  return (
    <CourseProgressStageLayout backUrl={courseUrl} className="mx-auto max-w-5xl space-y-5" testId="usi-number">
      <div className="wc-card overflow-hidden p-0">
        <div className="flex items-center justify-center gap-2 bg-[#0A2540] px-5 py-4 text-white">
          <ShieldCheck className="h-5 w-5" />
          <h1 className="font-display text-lg font-extrabold">USI Number</h1>
        </div>
        <div className="border-b-4 border-orange-500 bg-slate-50 px-5 py-2 text-center text-xs font-semibold text-slate-600">
          Unique Student Identifier workflow
        </div>
        <div className="space-y-5 p-5 sm:p-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-600">Submit the student USI for verification before identity documents are completed.</p>
              {workflow?.studentName && <p className="mt-1 text-sm font-semibold text-[#0A2540]">{workflow.studentName}</p>}
            </div>
            <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-xs font-bold ${statusClass(displayStatus || statusName)}`}>
              {displayStatus || statusName}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-10 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              {message && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">{message}</div>}

              {history.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <button type="button" className="mb-3 inline-flex items-center gap-2 text-sm font-extrabold text-[#183050]" onClick={() => setShowHistory((value) => !value)}>
                    <History className="h-4 w-4" /> {showHistory ? "Hide workflow history" : "Show workflow history"}
                  </button>
                  {showHistory && <WorkflowHistory rows={history} />}
                </div>
              )}

              <section className="rounded-lg border border-orange-200 bg-orange-50 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="font-display text-lg font-extrabold text-[#0A2540]">Current USI</h2>
                    <p className="mt-1 text-sm text-slate-600">{statusName || "Not submitted"}</p>
                  </div>
                  <BadgeCheck className="h-7 w-7 text-orange-600" />
                </div>

                <label className="mt-5 block text-sm font-bold text-[#0A2540]" htmlFor="usi-number-input">USI number</label>
                <input
                  id="usi-number-input"
                  type="text"
                  value={usiNumber}
                  onChange={(event) => setUsiNumber(event.target.value)}
                  disabled={isAdmin ? !canEditAdmin : !canEdit}
                  className="wc-input mt-2 max-w-md bg-white"
                />

                {canEditAdmin && (
                  <button type="button" className="wc-btn-primary mt-5 min-w-[150px] justify-center" disabled={saving === "usi"} onClick={saveAdminUSI}>
                    {saving === "usi" && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save USI
                  </button>
                )}

                {!isAdmin && !canEdit && (
                  <p className="mt-2 text-sm font-semibold text-slate-600">This USI is locked while it is waiting for admin review.</p>
                )}

                {!isAdmin && canEdit && (
                  <>
                    <label className="mt-4 block text-sm font-bold text-[#0A2540]" htmlFor="usi-remarks">Remarks</label>
                    <textarea id="usi-remarks" value={remarks} onChange={(event) => setRemarks(event.target.value)} className="wc-input mt-2 min-h-[88px] max-w-xl bg-white" />
                    <button type="button" className="wc-btn-primary mt-5 min-w-[170px] justify-center" disabled={saving === "submit"} onClick={submitUSI}>
                      {saving === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {workflow?.studentUSIId ? "Resubmit USI" : "Submit USI"}
                    </button>
                  </>
                )}

                {isAdmin && buttons.length > 0 && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {buttons.map((button) => {
                      const next = Number(button.nextWorkFlowStatusNo ?? button.NextWorkFlowStatusNo);
                      const text = button.text ?? button.Text ?? "Update";
                      return <button key={next} type="button" className={getActionClass(text)} disabled={saving === String(next)} onClick={() => runAdminAction(button)}>{saving === String(next) ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{text}</button>;
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </CourseProgressStageLayout>
  );
}

function WorkflowHistory({ rows }) { return <WorkflowHistoryTimeline rows={rows} />; }

function getActionClass(text) {
  const lower = String(text).toLowerCase();
  const base = "inline-flex h-11 min-w-[120px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-extrabold transition-colors";
  if (lower.includes("verify") || lower.includes("exempt")) return `${base} border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700`;
  if (lower.includes("return") || lower.includes("incorrect")) return `${base} border border-rose-200 bg-white text-rose-700 hover:bg-rose-50`;
  return `${base} border border-[#183050] bg-white text-[#183050] hover:bg-slate-50`;
}
